import React, { useState } from 'react';
import { 
  Calendar, Gift, Users, Sun, CloudRain,
  TrendingUp, Package, AlertCircle, CheckCircle
} from 'lucide-react';
import { colors } from '../../../constants/theme';
import { Card } from '../../ui/Card';
import { Badge } from '../../ui/Badge';
import { Button } from '../../ui/Button';

interface StockItem {
  skuId: string;
  skuName: string;
  category: string;
  currentStock: number;
  optimalStock: number;
  minStock: number;
  maxStock: number;
  dailyDemand: number;
  leadTimeDays: number;
  reorderPoint: number;
  orderQuantity: number;
  stockDays: number;
  status: 'optimal' | 'low' | 'critical' | 'excess';
}

interface EventAdjustment {
  eventId: string;
  eventName: string;
  eventDate: string;
  eventType: 'holiday' | 'sale' | 'weather' | 'local';
  expectedImpact: number; // -100 to +100
  affectedCategories: string[];
  adjustmentRecommendations: {
    category: string;
    currentMultiplier: number;
    recommendedMultiplier: number;
    reason: string;
  }[];
}

interface EventStockAdjustmentProps {
  stockItems: StockItem[];
  eventAdjustments?: any[];
}

const EventStockAdjustment: React.FC<EventStockAdjustmentProps> = ({ 
  stockItems, 
  eventAdjustments = [] 
}) => {
  const [selectedEvent, setSelectedEvent] = useState<string>('');
  const [adjustmentStatus, setAdjustmentStatus] = useState<Record<string, boolean>>({});

  // イベント情報（モック）
  const upcomingEvents: EventAdjustment[] = [
    {
      eventId: 'EVT001',
      eventName: '秋の大感謝祭',
      eventDate: '2025-10-01',
      eventType: 'sale',
      expectedImpact: 45,
      affectedCategories: ['全カテゴリ'],
      adjustmentRecommendations: [
        { category: '飲料', currentMultiplier: 1.0, recommendedMultiplier: 1.5, reason: 'セール期間中の需要増' },
        { category: '菓子', currentMultiplier: 1.0, recommendedMultiplier: 1.4, reason: 'ファミリー層の来店増' },
        { category: '日配品', currentMultiplier: 1.0, recommendedMultiplier: 1.3, reason: '特売商品の需要増' }
      ]
    },
    {
      eventId: 'EVT002',
      eventName: '台風接近',
      eventDate: '2025-09-25',
      eventType: 'weather',
      expectedImpact: -30,
      affectedCategories: ['生鮮'],
      adjustmentRecommendations: [
        { category: '生鮮', currentMultiplier: 1.0, recommendedMultiplier: 0.7, reason: '来店客数減少予測' },
        { category: '保存食', currentMultiplier: 1.0, recommendedMultiplier: 1.8, reason: '防災需要の急増' },
        { category: '飲料', currentMultiplier: 1.0, recommendedMultiplier: 1.5, reason: '備蓄需要' }
      ]
    },
    {
      eventId: 'EVT003',
      eventName: 'ハロウィン',
      eventDate: '2025-10-31',
      eventType: 'holiday',
      expectedImpact: 35,
      affectedCategories: ['菓子'],
      adjustmentRecommendations: [
        { category: '菓子', currentMultiplier: 1.0, recommendedMultiplier: 2.0, reason: 'ハロウィン商品の需要急増' },
        { category: 'パーティー用品', currentMultiplier: 1.0, recommendedMultiplier: 1.6, reason: 'イベント需要' }
      ]
    },
    {
      eventId: 'EVT004',
      eventName: '地域祭り',
      eventDate: '2025-09-27',
      eventType: 'local',
      expectedImpact: 20,
      affectedCategories: ['飲料', '軽食'],
      adjustmentRecommendations: [
        { category: '飲料', currentMultiplier: 1.0, recommendedMultiplier: 1.3, reason: '屋外イベントによる需要増' },
        { category: '軽食', currentMultiplier: 1.0, recommendedMultiplier: 1.25, reason: '持ち歩き商品の需要' }
      ]
    }
  ];

  const getEventIcon = (type: string) => {
    switch (type) {
      case 'sale': return <Gift size={16} style={{ color: colors.accent.red }} />;
      case 'weather': return <CloudRain size={16} style={{ color: colors.accent.blue }} />;
      case 'holiday': return <Calendar size={16} style={{ color: colors.accent.purple }} />;
      case 'local': return <Users size={16} style={{ color: colors.support.success }} />;
      default: return <Calendar size={16} />;
    }
  };

  const applyAdjustment = (eventId: string) => {
    setAdjustmentStatus(prev => ({ ...prev, [eventId]: true }));
    // 実際の在庫調整処理をシミュレート
    setTimeout(() => {
      console.log(`Event ${eventId} adjustments applied`);
    }, 1000);
  };

  // カテゴリ別の在庫影響計算
  const calculateCategoryImpact = (category: string) => {
    const categoryItems = stockItems.filter(item => item.category === category);
    const totalStock = categoryItems.reduce((sum, item) => sum + item.currentStock, 0);
    const totalDemand = categoryItems.reduce((sum, item) => sum + item.dailyDemand, 0);
    const stockDays = totalStock / totalDemand;
    
    return {
      itemCount: categoryItems.length,
      totalStock,
      totalDemand,
      stockDays: Math.floor(stockDays),
      riskLevel: stockDays < 3 ? 'high' : stockDays < 7 ? 'medium' : 'low'
    };
  };

  return (
    <div className="space-y-6">
      {/* イベントカレンダー */}
      <Card>
        <div className="p-4 border-b" style={{ borderColor: colors.ui.border }}>
          <h3 className="font-semibold" style={{ color: colors.text.primary }}>
            今後のイベント影響予測
          </h3>
        </div>
        
        <div className="p-4">
          <div className="grid grid-cols-2 gap-4">
            {upcomingEvents.map(event => {
              const isApplied = adjustmentStatus[event.eventId];
              const daysUntil = Math.floor((new Date(event.eventDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
              
              return (
                <div 
                  key={event.eventId}
                  className={`border rounded-lg p-4 cursor-pointer transition-all ${
                    selectedEvent === event.eventId ? 'shadow-lg' : 'hover:shadow-md'
                  }`}
                  style={{ 
                    borderColor: selectedEvent === event.eventId ? colors.accent.blue : colors.ui.border,
                    backgroundColor: isApplied ? colors.support.success + '10' : 'transparent'
                  }}
                  onClick={() => setSelectedEvent(event.eventId)}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      {getEventIcon(event.eventType)}
                      <h4 className="font-medium" style={{ color: colors.text.primary }}>
                        {event.eventName}
                      </h4>
                      {isApplied && (
                        <CheckCircle size={16} style={{ color: colors.support.success }} />
                      )}
                    </div>
                    <Badge 
                      variant={daysUntil <= 3 ? 'error' : daysUntil <= 7 ? 'warning' : 'secondary'}
                    >
                      {daysUntil}日後
                    </Badge>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="text-sm">
                      <span style={{ color: colors.text.secondary }}>日付: </span>
                      <span style={{ color: colors.text.primary }}>
                        {new Date(event.eventDate).toLocaleDateString('ja-JP')}
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <TrendingUp size={14} style={{ 
                        color: event.expectedImpact > 0 ? colors.support.success : colors.status.error 
                      }} />
                      <span className="text-sm font-medium" style={{ 
                        color: event.expectedImpact > 0 ? colors.support.success : colors.status.error 
                      }}>
                        需要予測: {event.expectedImpact > 0 ? '+' : ''}{event.expectedImpact}%
                      </span>
                    </div>
                    
                    <div className="flex flex-wrap gap-1">
                      {event.affectedCategories.map(cat => (
                        <Badge key={cat} variant="secondary" size="small">
                          {cat}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </Card>

      {/* 選択されたイベントの詳細 */}
      {selectedEvent && (
        <Card>
          <div className="p-4 border-b" style={{ borderColor: colors.ui.border }}>
            <h3 className="font-semibold" style={{ color: colors.text.primary }}>
              在庫調整推奨
            </h3>
          </div>
          
          <div className="p-4">
            {(() => {
              const event = upcomingEvents.find(e => e.eventId === selectedEvent);
              if (!event) return null;
              
              return (
                <>
                  <div className="mb-4 p-3 rounded-lg" style={{ backgroundColor: colors.ui.bgSecondary }}>
                    <div className="flex items-center gap-2 mb-2">
                      {getEventIcon(event.eventType)}
                      <span className="font-medium">{event.eventName}</span>
                      <span className="text-sm" style={{ color: colors.text.secondary }}>
                        ({event.eventDate})
                      </span>
                    </div>
                    <p className="text-sm" style={{ color: colors.text.secondary }}>
                      このイベントに向けて、以下のカテゴリで在庫調整を推奨します。
                    </p>
                  </div>
                  
                  <div className="space-y-3">
                    {event.adjustmentRecommendations.map(rec => {
                      const impact = calculateCategoryImpact(rec.category);
                      
                      return (
                        <div key={rec.category} className="border rounded-lg p-4" style={{ borderColor: colors.ui.border }}>
                          <div className="flex items-center justify-between mb-3">
                            <div>
                              <h4 className="font-medium" style={{ color: colors.text.primary }}>
                                {rec.category}
                              </h4>
                              <p className="text-sm" style={{ color: colors.text.secondary }}>
                                {rec.reason}
                              </p>
                            </div>
                            <Badge 
                              variant={impact.riskLevel === 'high' ? 'error' : 
                                      impact.riskLevel === 'medium' ? 'warning' : 'success'}
                            >
                              リスク: {impact.riskLevel === 'high' ? '高' : 
                                      impact.riskLevel === 'medium' ? '中' : '低'}
                            </Badge>
                          </div>
                          
                          <div className="grid grid-cols-4 gap-4 mb-3">
                            <div>
                              <span className="text-xs" style={{ color: colors.text.secondary }}>対象品目</span>
                              <div className="text-sm font-medium">{impact.itemCount}品</div>
                            </div>
                            <div>
                              <span className="text-xs" style={{ color: colors.text.secondary }}>現在庫</span>
                              <div className="text-sm font-medium">{impact.totalStock.toLocaleString()}</div>
                            </div>
                            <div>
                              <span className="text-xs" style={{ color: colors.text.secondary }}>日次需要</span>
                              <div className="text-sm font-medium">{impact.totalDemand}/日</div>
                            </div>
                            <div>
                              <span className="text-xs" style={{ color: colors.text.secondary }}>在庫日数</span>
                              <div className="text-sm font-medium">{impact.stockDays}日分</div>
                            </div>
                          </div>
                          
                          <div className="flex items-center justify-between p-3 rounded" style={{ backgroundColor: colors.accent.blue + '10' }}>
                            <div className="flex items-center gap-4">
                              <div>
                                <span className="text-xs" style={{ color: colors.text.secondary }}>現在の係数</span>
                                <div className="text-lg font-bold">{rec.currentMultiplier.toFixed(1)}x</div>
                              </div>
                              <AlertCircle size={20} style={{ color: colors.accent.blue }} />
                              <div>
                                <span className="text-xs" style={{ color: colors.text.secondary }}>推奨係数</span>
                                <div className="text-lg font-bold" style={{ color: colors.accent.blue }}>
                                  {rec.recommendedMultiplier.toFixed(1)}x
                                </div>
                              </div>
                            </div>
                            <div className="text-sm">
                              <span style={{ color: colors.text.secondary }}>発注増加量: </span>
                              <span className="font-bold" style={{ color: colors.accent.blue }}>
                                +{Math.round((rec.recommendedMultiplier - rec.currentMultiplier) * 100)}%
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  <div className="mt-4 flex justify-end gap-2">
                    <Button variant="secondary">
                      シミュレーション
                    </Button>
                    <Button 
                      variant="primary"
                      onClick={() => applyAdjustment(event.eventId)}
                      disabled={adjustmentStatus[event.eventId]}
                    >
                      {adjustmentStatus[event.eventId] ? '適用済み' : '調整を適用'}
                    </Button>
                  </div>
                </>
              );
            })()}
          </div>
        </Card>
      )}

      {/* イベント影響シミュレーション */}
      <Card>
        <div className="p-4 border-b" style={{ borderColor: colors.ui.border }}>
          <h3 className="font-semibold" style={{ color: colors.text.primary }}>
            イベント影響シミュレーション
          </h3>
        </div>
        
        <div className="p-4">
          <div className="space-y-3">
            {['飲料', '菓子', '日配品'].map(category => {
              const impact = calculateCategoryImpact(category);
              const hasUpcomingEvent = upcomingEvents.some(e => 
                e.affectedCategories.includes(category) || e.affectedCategories.includes('全カテゴリ')
              );
              
              return (
                <div key={category} className="flex items-center justify-between p-3 rounded" 
                  style={{ backgroundColor: colors.ui.bgSecondary }}
                >
                  <div className="flex items-center gap-3">
                    <Package size={18} style={{ color: colors.text.secondary }} />
                    <div>
                      <div className="font-medium" style={{ color: colors.text.primary }}>
                        {category}
                      </div>
                      <div className="text-xs" style={{ color: colors.text.secondary }}>
                        {impact.itemCount}品目 / 在庫{impact.stockDays}日分
                      </div>
                    </div>
                  </div>
                  
                  {hasUpcomingEvent && (
                    <div className="flex items-center gap-2">
                      <AlertCircle size={16} style={{ color: colors.status.warning }} />
                      <span className="text-sm" style={{ color: colors.status.warning }}>
                        イベント影響あり
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </Card>
    </div>
  );
};

export default EventStockAdjustment;
